import { useState } from 'react'
import { useRoomStore } from '@/lib/store/room-store'
import { requireClient } from '@/lib/matrix/client'
import { toast } from '@/components/ui/Toast'
import { MsgType } from 'matrix-js-sdk'
import { Avatar } from '@/components/ui/Avatar'
import type { SerializedEvent } from '@/lib/matrix/events'

interface ForwardDialogProps {
  event: SerializedEvent
  onClose: () => void
}

export function ForwardDialog({ event, onClose }: ForwardDialogProps) {
  const rooms = useRoomStore((s) => s.rooms)
  const [filter, setFilter] = useState('')
  const [forwarding, setForwarding] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const roomList = Array.from(rooms.values())
    .filter((r) => r.membership === 'join')
    .filter((r) => !filter || r.name.toLowerCase().includes(filter.toLowerCase()))
    .sort((a, b) => (b.lastEvent?.timestamp ?? 0) - (a.lastEvent?.timestamp ?? 0))

  const handleForward = async (targetRoomId: string) => {
    setForwarding(targetRoomId)
    try {
      const client = requireClient()
      const body = (event.content.body as string) ?? ''
      const msgtype = (event.content.msgtype as string) ?? MsgType.Text

      // Native targets: tag with our Matrix-only marker so we render a "Forwarded"
      // label (Matrix has no standard forward flag). Bridged targets (Signal etc.)
      // drop unknown fields and only relay body text — so add a textual attribution
      // line instead, and skip the marker (it'd just duplicate the visible line).
      const sourceName = rooms.get(event.roomId)?.name
      const bridged = !!rooms.get(targetRoomId)?.bridgeType
      const marker = bridged ? {} : { 'io.chatpulse.forwarded': sourceName ? { from: sourceName } : true }
      const attribution = `↪ Forwarded from ${event.senderName}`

      const isMedia = msgtype === 'm.image' || msgtype === 'm.video' || msgtype === 'm.audio' || msgtype === 'm.file'

      if (isMedia) {
        if (bridged) await client.sendTextMessage(targetRoomId, attribution)
        const url = (event.content.url as string) ?? (event.content.file as Record<string, unknown>)?.url as string
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await client.sendMessage(targetRoomId, {
          msgtype: msgtype as MsgType,
          body,
          url,
          info: event.content.info,
          ...(event.content.file ? { file: event.content.file } : {}),
          ...marker,
        } as any)
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await client.sendMessage(targetRoomId, {
          msgtype: MsgType.Text,
          body: bridged ? `${attribution}:\n${body}` : body,
          // Keep rich formatting for native targets; bridges only use plain text.
          ...(!bridged && event.content.format ? { format: event.content.format, formatted_body: event.content.formatted_body } : {}),
          ...marker,
        } as any)
      }
      setDone(true)
      toast('Message forwarded', 'success')
      setTimeout(onClose, 800)
    } catch {
      setForwarding(null)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-sm bg-[rgb(var(--np-surface))] border border-[rgb(var(--np-border))] rounded-lg overflow-hidden animate-slide-up max-h-[60vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-[rgb(var(--np-border))]">
          <h3 className="text-sm font-semibold text-[rgb(var(--np-tx-primary))] mb-2">Forward message</h3>
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search rooms..."
            autoFocus
            className="w-full bg-[rgb(var(--np-raised))] border border-[rgb(var(--np-border))] rounded-md px-3 py-2 text-sm text-[rgb(var(--np-tx-primary))] placeholder-[rgb(var(--np-tx-muted))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--np-accent)/0.5)] transition-all"
          />
        </div>

        {done ? (
          <div className="p-8 text-center text-sm text-[rgb(var(--np-status-ok))]">Forwarded!</div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {roomList.map((r) => (
              <button
                key={r.roomId}
                onClick={() => handleForward(r.roomId)}
                disabled={forwarding !== null}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[rgb(var(--np-raised))] transition-colors text-left disabled:opacity-50"
              >
                <Avatar mxc={r.avatarUrl} name={r.name} userId={r.roomId} size={32} />
                <span className="text-sm text-[rgb(var(--np-tx-primary))] truncate flex-1">{r.name}</span>
                {forwarding === r.roomId && (
                  <span className="text-xs text-[rgb(var(--np-tx-muted))]">Sending...</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
